import React from 'react';
import { Button, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { MUITableColumn } from 'components/shared/MUITable';
import { SmartIntakeHistoryRow } from './SmartIntakeTypes';

const OUTCOME_COLOR: Record<SmartIntakeHistoryRow['outcome'], string> = {
  confirmed: '#66bb6a',
  rejected: '#ff5252',
  failed: '#ff5252',
};

interface ActionHandlers {
  onViewDetails: (row: SmartIntakeHistoryRow) => void;
}

export const createSmartIntakeHistoryTableColumns = ({
  onViewDetails,
}: ActionHandlers): MUITableColumn<SmartIntakeHistoryRow>[] => [
  {
    header: 'Reviewed on',
    sortKey: 'reviewedDateSortValue',
    renderKey: 'reviewedDate',
    cellProps: { sx: { minWidth: 200, width: 200 } },
  },
  {
    header: 'Name',
    sortKey: 'name',
    renderKey: 'name',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Outcome',
    sortKey: 'outcomeLabel',
    renderKey: 'outcomeLabel',
    cellProps: { sx: { minWidth: 200, width: 200 } },
    renderCell: row => {
      const color = OUTCOME_COLOR[row.outcome];
      const Icon = row.outcome === 'confirmed' ? CheckCircleIcon : CloseIcon;
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Icon sx={{ fontSize: 18, color }} />
          <Typography variant="body2" sx={{ color, fontWeight: 500 }}>
            {row.outcomeLabel}
          </Typography>
        </Stack>
      );
    },
  },
  {
    header: 'Reviewed by',
    sortKey: 'reviewedByName',
    renderKey: 'reviewedByName',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Uploaded by',
    sortKey: 'uploadedBy',
    renderKey: 'uploadedBy',
    cellProps: { sx: { flex: 1 } },
  },
  {
    header: 'Related Submission',
    renderKey: 'submissionLabel',
    cellProps: { sx: { flex: 1 } },
    renderCell: row => {
      if (!row.submissionLabel) {
        return (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Not created
          </Typography>
        );
      }
      if (!row.submissionUrl) {
        return <Typography variant="body2">{row.submissionLabel}</Typography>;
      }
      return (
        <MuiLink component={RouterLink} to={row.submissionUrl} variant="body2">
          {row.submissionLabel}
        </MuiLink>
      );
    },
  },
  {
    header: 'Action',
    renderKey: 'id',
    cellProps: { sx: { minWidth: 116, width: 116 } },
    renderCell: row => (
      <Button size="small" variant="text" onClick={() => onViewDetails(row)}>
        View Details
      </Button>
    ),
  },
];
